/**
 * Capacitor integration utilities for Xpansiv Mobile App
 * Provides web-first implementations with mobile enhancements
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Check if running in a Capacitor native environment
 */
export const isNativeApp = () => Capacitor.isNativePlatform();

/**
 * Check if running on a specific platform
 */
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Initialize Capacitor plugins for native apps
 */
export const initializeCapacitor = async () => {
	if (!isNativeApp()) {
		console.log('Running in web mode - Capacitor plugins not available');
		return;
	}

	try {
		// Overlay the status bar on top of the webview so env(safe-area-inset-top)
		// reports the real notch/Dynamic Island inset; each header then pads itself
		// below it (MOB-160). With overlay:false iOS zeroes that inset, killing the
		// header padding — which is why the logo kept landing under the island.
		// Style.Light = dark icons, readable on the white header that now extends up
		// behind the status bar.
		await StatusBar.setOverlaysWebView({ overlay: true });
		await StatusBar.setStyle({ style: Style.Light });

		// Hide splash screen after app is ready
		await SplashScreen.hide();

		// Add app state change listeners
		App.addListener('appStateChange', async ({ isActive }) => {
			console.log('App state changed. Is active:', isActive);
			if (isActive) {
				try {
					const { authService } = await import('$lib/auth/service');
					await authService.checkAndRefreshToken();
				} catch (error) {
					console.error('[Capacitor] Failed to check token on resume:', error);
				}
			}
		});

		App.addListener('backButton', ({ canGoBack }) => {
			if (!canGoBack) {
				App.exitApp();
			} else {
				window.history.back();
			}
		});

		console.log('Capacitor initialized successfully');
	} catch (error) {
		console.error('Error initializing Capacitor:', error);
	}
};

/**
 * Show splash screen (useful for manual control)
 */
export const showSplash = async () => {
	if (isNativeApp()) {
		await SplashScreen.show();
	}
};

/**
 * Hide splash screen (useful for manual control)
 */
export const hideSplash = async () => {
	if (isNativeApp()) {
		await SplashScreen.hide();
	}
};

/**
 * Set status bar style (light or dark)
 */
export const setStatusBarStyle = async (isDark: boolean = true) => {
	if (isNativeApp()) {
		await StatusBar.setStyle({
			style: isDark ? Style.Dark : Style.Light
		});
	}
};

/**
 * Get app information
 */
export const getAppInfo = async () => {
	if (isNativeApp()) {
		const info = await App.getInfo();
		return {
			name: info.name,
			id: info.id,
			version: info.version,
			build: info.build,
			platform: getPlatform()
		};
	}

	return {
		name: 'Xpansiv Mobile',
		id: 'com.xpansiv.srectrade',
		version: '0.0.1',
		build: 'web',
		platform: 'web'
	};
};

/**
 * Exit the app (native only)
 */
export const exitApp = () => {
	if (isNativeApp()) {
		App.exitApp();
	} else {
		window.close();
	}
};
